import { useCallback, useEffect, useState } from "react";
import {
  Boxes, Loader2, PackageCheck, PackagePlus, Plus, ShieldAlert, Truck, Warehouse,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, stopRow, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
const BAD = "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
const INFO = "border-primary/40 bg-primary/10 text-primary";
const VET = { approved: OK, pending: WARN, blocked: BAD };
const PO = { draft: WARN, approved: INFO, part_received: WARN, received: OK, cancelled: BAD };

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const BLANK_SUP = { name: "", category: "goods", country: "IN", contact_name: "", email: "",
  phone: "", tax_id: "", payment_terms: "net_30", notes: "" };
const BLANK_PO = { supplier_id: "", fund_id: "", programme_code: "", warehouse_code: "WH-MUMBAI",
  currency: "INR", expected_on: "", notes: "",
  lines: [{ item: "", unit: "kit", quantity: 1, unit_cost: 0 }] };
const BLANK_DIST = { item: "", warehouse_code: "WH-MUMBAI", quantity: 1, programme_code: "",
  beneficiary_id: "", location: "", acknowledged_by: "", notes: "" };

export default function SupplyChain() {
  const { can } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState("");
  const [supplier, setSupplier] = useState(BLANK_SUP);
  const [po, setPo] = useState(BLANK_PO);
  const [dist, setDist] = useState(BLANK_DIST);
  const [receiving, setReceiving] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const detail = useRowDetail("", () => load());
  const [receiptLines, setReceiptLines] = useState([]);
  const [trace, setTrace] = useState(null);

  const load = useCallback(async () => {
    const [s, p, st, d, funds, programs, bens] = await Promise.all([
      api.get("/supply/suppliers"), api.get("/supply/purchase-orders"), api.get("/supply/stock"),
      api.get("/supply/distributions"), api.get("/charity/funds"), api.get("/charity/programs"),
      api.get("/beneficiaries"),
    ]);
    setState({ suppliers: s.data, pos: p.data, stock: st.data, dists: d.data,
      funds: funds.data, programs: programs.data,
      beneficiaries: (bens.data.items || bens.data || []).filter((b) => b.consent?.given) });
  }, []);

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      if (message) toast.success(typeof message === "function" ? message(res) : message);
      setDialog("");
      await load();
      return res;
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!state) return <Loading label="Loading the supply chain" />;
  const { suppliers, pos, stock, dists, funds, programs, beneficiaries } = state;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-024 · Supply chain"
        title="From the supplier's invoice to the hand that received the goods"
        description="Suppliers are screened against the watchlist before you can order from them, purchase orders need four-eyes approval, goods received are booked as batch-and-expiry stock lots with the cost posted to the paying fund, and every distribution picks first-expiry-first-out and names the person who signed for it."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-suppliers" label="Suppliers" accent="sky" icon={Truck}
             value={suppliers.counters.total} sub={`${suppliers.counters.blocked} blocked`} />
        <Kpi testId="kpi-po-open" label="Open order value" accent="violet" icon={PackagePlus}
             value={money(pos.counters.open_value, "INR", true)} />
        <Kpi testId="kpi-stock-value" label="Stock on hand" accent="emerald" icon={Boxes}
             value={money(stock.counters.value, "INR", true)} sub={`${stock.counters.skus} SKUs`} />
        <Kpi testId="kpi-stock-expiring" label="Lots expiring" accent="amber" icon={ShieldAlert}
             value={stock.counters.expiring} sub={`${stock.counters.expired} expired`} />
        <Kpi testId="kpi-distributions" label="Distributions" accent="teal" icon={PackageCheck}
             value={dists.counters.total} sub={money(dists.counters.value, "INR", true)} />
        <Kpi testId="kpi-unacknowledged" label="Unsigned receipts" accent="rose"
             value={dists.counters.unacknowledged} />
      </div>

      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Warehouse stock</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Purchase orders</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="distribution" data-testid="tab-distribution">Distribution</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="mt-4">
          <Panel testId="panel-stock" kicker="FR-PGM-008" title="Stock by item and warehouse">
            <Table testId="stock-table"
                   head={<><Th>Item</Th><Th>Warehouse</Th><Th>On hand</Th><Th>Lots</Th><Th>Value</Th>
                     <Th>Earliest expiry</Th><Th>Alerts</Th></>}>
              {stock.items.length === 0 && <Empty colSpan={7} label="No stock on hand" />}
              {stock.items.map((r) => (
                <tr key={`${r.item}-${r.warehouse_code}`} className="row-hover cursor-pointer"
                    onClick={rowClick(() => detail.show(r, `${r.item} · ${r.warehouse_code}`,
                                                        r.item, "Warehouse stock"))}
                    data-testid={`stock-row-${r.warehouse_code}-${r.item.replace(/\s+/g, "-").toLowerCase()}`}>
                  <Td className="font-medium text-foreground">{r.item}</Td>
                  <Td><Chip className={INFO}>{r.warehouse_code}</Chip></Td>
                  <Td className="font-mono text-[11px]">{r.quantity} {r.unit}</Td>
                  <Td className="font-mono text-[11px]">{r.lots}</Td>
                  <Td className="font-mono text-[11px]">{money(r.value, "INR", true)}</Td>
                  <Td className="font-mono text-[11px]">{r.earliest_expiry || "—"}</Td>
                  <Td>
                    {r.expired > 0 && <Chip className={BAD}>{r.expired} expired</Chip>}
                    {r.expiring > 0 && <Chip className={WARN}>{r.expiring} expiring</Chip>}
                    {!r.expired && !r.expiring && <Chip className={OK}>clear</Chip>}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <Panel testId="panel-orders" kicker="FR-PGM-007" title="Purchase orders"
                 actions={can("supply", "write") && (
                   <Dialog open={dialog === "po"} onOpenChange={(o) => setDialog(o ? "po" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-po-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> Raise order
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-2xl border-border bg-card">
                       <DialogHeader><DialogTitle>Raise a purchase order</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <F label="Supplier">
                           <Select value={po.supplier_id} onValueChange={(v) => setPo({ ...po, supplier_id: v })}>
                             <SelectTrigger data-testid="po-supplier-select" className="bg-muted/40">
                               <SelectValue placeholder="Choose a supplier" /></SelectTrigger>
                             <SelectContent>{suppliers.items.map((s) => (
                               <SelectItem key={s.id} value={s.id} disabled={s.vetting_status === "blocked"}>
                                 {s.name}{s.vetting_status === "blocked" ? " · blocked by vetting" : ""}
                               </SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Paying fund">
                           <Select value={po.fund_id} onValueChange={(v) => setPo({ ...po, fund_id: v })}>
                             <SelectTrigger data-testid="po-fund-select" className="bg-muted/40">
                               <SelectValue placeholder="Fund" /></SelectTrigger>
                             <SelectContent>{funds.map((f) => (
                               <SelectItem key={f.id} value={f.id}>{f.code}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Warehouse">
                           <Select value={po.warehouse_code} onValueChange={(v) => setPo({ ...po, warehouse_code: v })}>
                             <SelectTrigger data-testid="po-warehouse-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent><SelectItem value="WH-MUMBAI">WH-MUMBAI</SelectItem>
                               <SelectItem value="WH-NAGPUR">WH-NAGPUR</SelectItem></SelectContent>
                           </Select>
                         </F>
                         <F label="Programme">
                           <Select value={po.programme_code} onValueChange={(v) => setPo({ ...po, programme_code: v })}>
                             <SelectTrigger data-testid="po-programme-select" className="bg-muted/40">
                               <SelectValue placeholder="Optional" /></SelectTrigger>
                             <SelectContent>{programs.map((p) => (
                               <SelectItem key={p.id} value={p.code}>{p.code}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                       </div>
                       <div className="space-y-2">
                         <Label className="text-[11px] text-muted-foreground">Lines</Label>
                         {po.lines.map((l, i) => (
                           <div key={i} className="grid grid-cols-[1fr_90px_80px_100px] gap-2">
                             <Input value={l.item} placeholder="Item" data-testid={`po-item-${i}`}
                                    onChange={(e) => { const lines = [...po.lines]; lines[i] = { ...l, item: e.target.value }; setPo({ ...po, lines }); }}
                                    className="bg-muted/40 text-[12px]" />
                             <Select value={l.unit} onValueChange={(v) => { const lines = [...po.lines]; lines[i] = { ...l, unit: v }; setPo({ ...po, lines }); }}>
                               <SelectTrigger data-testid={`po-unit-${i}`} className="bg-muted/40 text-[12px]"><SelectValue /></SelectTrigger>
                               <SelectContent>{["kit", "kg", "litre", "unit", "pack", "dose", "box"].map((u) => (
                                 <SelectItem key={u} value={u}>{u}</SelectItem>))}</SelectContent>
                             </Select>
                             <Input type="number" value={l.quantity} data-testid={`po-qty-${i}`}
                                    onChange={(e) => { const lines = [...po.lines]; lines[i] = { ...l, quantity: Number(e.target.value) }; setPo({ ...po, lines }); }}
                                    className="bg-muted/40 font-mono text-[12px]" />
                             <Input type="number" value={l.unit_cost} data-testid={`po-cost-${i}`}
                                    onChange={(e) => { const lines = [...po.lines]; lines[i] = { ...l, unit_cost: Number(e.target.value) }; setPo({ ...po, lines }); }}
                                    className="bg-muted/40 font-mono text-[12px]" />
                           </div>
                         ))}
                         <Button size="sm" variant="outline" data-testid="po-add-line"
                                 onClick={() => setPo({ ...po, lines: [...po.lines, { item: "", unit: "unit", quantity: 1, unit_cost: 0 }] })}
                                 className="h-7 rounded-full border-border text-[11px]">Add line</Button>
                       </div>
                       <Button data-testid="po-save-button"
                               disabled={busy === "po" || !po.supplier_id || !po.fund_id || !po.lines[0].item}
                               onClick={() => act("po", () => api.post("/supply/purchase-orders",
                                 { ...po, expected_on: po.expected_on || null }), "Purchase order raised")
                                 .then(() => setPo(BLANK_PO))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "po" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Raise order
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="po-table"
                   head={<><Th>Order</Th><Th>Supplier</Th><Th>Fund</Th><Th>Warehouse</Th><Th>Lines</Th>
                     <Th>Total</Th><Th>Status</Th><Th /></>}>
              {pos.items.length === 0 && <Empty colSpan={8} label="No purchase orders yet" />}
              {pos.items.map((p) => (
                <tr key={p.po_ref} className="row-hover cursor-pointer"
                    onClick={() => detail.show(p, p.po_ref, p.supplier_name, "Purchase order")}
                    data-testid={`po-row-${p.po_ref}`}>
                  <Td className="font-mono text-[11px]">{p.po_ref}</Td>
                  <Td className="font-medium text-foreground">{p.supplier_name}</Td>
                  <Td className="font-mono text-[11px]">{p.fund_code}</Td>
                  <Td className="font-mono text-[11px]">{p.warehouse_code}</Td>
                  <Td className="text-[11px]">{p.lines.length}</Td>
                  <Td className="font-mono text-[11px]">{money(p.total, p.currency, true)}</Td>
                  <Td><Chip testId={`po-status-${p.po_ref}`} className={PO[p.status]}>{p.status.replace("_", " ")}</Chip></Td>
                  <Td className="space-x-1.5" onClick={stopRow}>
                    {p.status === "draft" && can("supply", "approve") && (
                      <Button size="sm" variant="outline" data-testid={`approve-po-${p.po_ref}`}
                              disabled={busy === `ap-${p.po_ref}`}
                              onClick={() => act(`ap-${p.po_ref}`, () => api.post(`/supply/purchase-orders/${p.po_ref}/approve`), "Order approved")}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">Approve</Button>
                    )}
                    {["approved", "part_received"].includes(p.status) && can("supply", "write") && (
                      <Button size="sm" variant="outline" data-testid={`receive-po-${p.po_ref}`}
                              onClick={() => { setReceiving(p); setReceiptLines(p.lines.map((l) => ({ item: l.item, quantity: l.quantity - (l.received_quantity || 0), batch: "", expires_on: "" }))); }}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">Receive goods</Button>
                    )}
                    {["draft", "approved", "part_received"].includes(p.status) && can("supply", "write") && (
                      <Button size="sm" variant="outline" data-testid={`cancel-po-${p.po_ref}`}
                              onClick={() => { setCancelling(p); setCancelReason(""); }}
                              className="h-7 rounded-full border-red-500/40 px-2.5 text-[10px] text-red-600 dark:text-red-400">
                        Cancel
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4">
          <Panel testId="panel-suppliers" kicker="Sourcing" title="Supplier register"
                 actions={can("supply", "write") && (
                   <Dialog open={dialog === "sup"} onOpenChange={(o) => setDialog(o ? "sup" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-supplier-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> Add supplier
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-lg border-border bg-card">
                       <DialogHeader><DialogTitle>Add and screen a supplier</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <div className="col-span-2"><F label="Legal name"><Input value={supplier.name}
                            data-testid="supplier-name-input"
                            onChange={(e) => setSupplier({ ...supplier, name: e.target.value })}
                            className="bg-muted/40" /></F></div>
                         <F label="Category">
                           <Select value={supplier.category} onValueChange={(v) => setSupplier({ ...supplier, category: v })}>
                             <SelectTrigger data-testid="supplier-category-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>{["goods", "medical", "works", "services", "logistics"].map((x) => (
                               <SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Country"><Input value={supplier.country} data-testid="supplier-country-input"
                            onChange={(e) => setSupplier({ ...supplier, country: e.target.value.toUpperCase().slice(0, 2) })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Contact"><Input value={supplier.contact_name} data-testid="supplier-contact-input"
                            onChange={(e) => setSupplier({ ...supplier, contact_name: e.target.value })}
                            className="bg-muted/40" /></F>
                         <F label="Tax ID"><Input value={supplier.tax_id} data-testid="supplier-tax-input"
                            onChange={(e) => setSupplier({ ...supplier, tax_id: e.target.value })}
                            className="bg-muted/40 font-mono text-[12px]" /></F>
                       </div>
                       <Button data-testid="supplier-save-button" disabled={busy === "sup" || !supplier.name}
                               onClick={() => act("sup", () => api.post("/supply/suppliers", supplier),
                                 (r) => `Screened: ${r.data.screening.outcome} · vetting ${r.data.vetting_status}`)
                                 .then(() => setSupplier(BLANK_SUP))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "sup" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add and screen
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="suppliers-table"
                   head={<><Th>Supplier</Th><Th>Category</Th><Th>Country</Th><Th>Terms</Th><Th>Orders</Th>
                     <Th>Spend</Th><Th>Vetting</Th><Th /></>}>
              {suppliers.items.map((s) => (
                <tr key={refOf(s)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(s, refOf(s), "", "", "supplier"))} data-testid={`supplier-row-${s.id}`}>
                  <Td><div className="font-medium text-foreground">{s.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{s.code} · {s.email}</div></Td>
                  <Td className="text-[11px]">{s.category}</Td>
                  <Td className="font-mono text-[11px]">{s.country}</Td>
                  <Td className="text-[11px]">{(s.payment_terms || "").replace("_", " ")}</Td>
                  <Td className="font-mono text-[11px]">{s.orders}</Td>
                  <Td className="font-mono text-[11px]">{money(s.spend, "INR", true)}</Td>
                  <Td><Chip testId={`supplier-vetting-${s.id}`} className={VET[s.vetting_status]}>{s.vetting_status}</Chip>
                    {s.vetting_note && <div className="mt-1 max-w-[180px] text-[10px] text-muted-foreground">{s.vetting_note}</div>}
                  </Td>
                  <Td>
                    {can("supply", "approve") && (
                      <Select value="" onValueChange={(v) => act(`vet-${s.id}`,
                        () => api.post(`/supply/suppliers/${s.id}/vetting`, { status: v, note: "Reviewed from the supplier register" }),
                        `${s.name} marked ${v}`)}>
                        <SelectTrigger data-testid={`supplier-vet-${s.id}`} className="h-7 w-[110px] bg-muted/40 text-[11px]">
                          <SelectValue placeholder="Set vetting" /></SelectTrigger>
                        <SelectContent>{["approved", "pending", "blocked"].filter((x) => x !== s.vetting_status)
                          .map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="distribution" className="mt-4 space-y-4">
          <Panel testId="panel-issue" kicker="FR-PGM-009" title="Issue stock to beneficiaries">
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
              <F label="Item">
                <Select value={dist.item} onValueChange={(v) => setDist({ ...dist, item: v })}>
                  <SelectTrigger data-testid="dist-item-select" className="bg-muted/40">
                    <SelectValue placeholder="Item" /></SelectTrigger>
                  <SelectContent>{[...new Set(stock.items.map((s) => s.item))].map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>))}</SelectContent>
                </Select>
              </F>
              <F label="Warehouse">
                <Select value={dist.warehouse_code} onValueChange={(v) => setDist({ ...dist, warehouse_code: v })}>
                  <SelectTrigger data-testid="dist-warehouse-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="WH-MUMBAI">WH-MUMBAI</SelectItem>
                    <SelectItem value="WH-NAGPUR">WH-NAGPUR</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Quantity"><Input type="number" value={dist.quantity} data-testid="dist-qty-input"
                 onChange={(e) => setDist({ ...dist, quantity: Number(e.target.value) })}
                 className="bg-muted/40 font-mono" /></F>
              <F label="Beneficiary">
                <Select value={dist.beneficiary_id} onValueChange={(v) => setDist({ ...dist, beneficiary_id: v })}>
                  <SelectTrigger data-testid="dist-beneficiary-select" className="bg-muted/40">
                    <SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="max-h-64">{beneficiaries.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.code} · {b.district || b.country}</SelectItem>))}</SelectContent>
                </Select>
              </F>
              <F label="Signed for by"><Input value={dist.acknowledged_by} data-testid="dist-ack-input"
                 onChange={(e) => setDist({ ...dist, acknowledged_by: e.target.value })}
                 className="bg-muted/40" /></F>
              <div className="flex items-end">
                <Button data-testid="dist-save-button"
                        disabled={busy === "dist" || !dist.item || !dist.quantity || !can("supply", "write")}
                        onClick={() => act("dist", () => api.post("/supply/distributions", dist),
                          (r) => `${r.data.distribution_ref} issued from ${r.data.lots.length} lot(s)`)
                          .then(() => setDist(BLANK_DIST))}
                        className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "dist" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Issue
                </Button>
              </div>
            </div>
            <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              Stock is picked first-expiry-first-out. A beneficiary with no recorded consent cannot be
              logged against a distribution.
            </div>
          </Panel>

          <Panel testId="panel-distributions" kicker="Register" title="Distributions">
            <Table testId="distributions-table"
                   head={<><Th>Reference</Th><Th>Item</Th><Th>Quantity</Th><Th>Beneficiary</Th>
                     <Th>Value</Th><Th>Date</Th><Th>Signed by</Th><Th /></>}>
              {dists.items.length === 0 && <Empty colSpan={8} label="Nothing distributed yet" />}
              {dists.items.map((d) => (
                <tr key={refOf(d)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(d, refOf(d)))} data-testid={`dist-row-${d.distribution_ref}`}>
                  <Td className="font-mono text-[11px]">{d.distribution_ref}</Td>
                  <Td className="font-medium text-foreground">{d.item}</Td>
                  <Td className="font-mono text-[11px]">{d.quantity} {d.unit}</Td>
                  <Td className="font-mono text-[11px]">{d.beneficiary_code || "—"}</Td>
                  <Td className="font-mono text-[11px]">{money(d.value, "INR", true)}</Td>
                  <Td className="font-mono text-[11px]">{shortDate(d.distributed_on)}</Td>
                  <Td>{d.acknowledged_by
                    ? <Chip className={OK}>{d.acknowledged_by}</Chip>
                    : <Chip className={WARN}>unsigned</Chip>}</Td>
                  <Td>
                    <Button size="sm" variant="outline" data-testid={`trace-${d.distribution_ref}`}
                            onClick={async () => {
                              const r = await api.get(`/supply/traceability/${d.distribution_ref}`);
                              setTrace(r.data);
                            }}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">Trace</Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(receiving)} onOpenChange={(o) => !o && setReceiving(null)}>
        <DialogContent className="max-w-lg border-border bg-card" data-testid="receive-dialog">
          <DialogHeader><DialogTitle>Receive goods on {receiving?.po_ref}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {receiptLines.map((l, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-border p-3">
                <div className="text-[12.5px] font-medium text-foreground">{l.item}</div>
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" value={l.quantity} data-testid={`grn-qty-${i}`}
                         onChange={(e) => { const n = [...receiptLines]; n[i] = { ...l, quantity: Number(e.target.value) }; setReceiptLines(n); }}
                         className="bg-muted/40 font-mono text-[12px]" />
                  <Input value={l.batch} placeholder="Batch" data-testid={`grn-batch-${i}`}
                         onChange={(e) => { const n = [...receiptLines]; n[i] = { ...l, batch: e.target.value }; setReceiptLines(n); }}
                         className="bg-muted/40 font-mono text-[12px]" />
                  <Input type="date" value={l.expires_on} data-testid={`grn-expiry-${i}`}
                         onChange={(e) => { const n = [...receiptLines]; n[i] = { ...l, expires_on: e.target.value }; setReceiptLines(n); }}
                         className="bg-muted/40 text-[12px]" />
                </div>
              </div>
            ))}
            <Button data-testid="grn-save-button" disabled={busy === "grn"}
                    onClick={() => act("grn", () => api.post(`/supply/purchase-orders/${receiving.po_ref}/receive`,
                      { lines: receiptLines.filter((l) => l.quantity > 0).map((l) => ({ ...l, expires_on: l.expires_on || null })) }),
                      (r) => `${r.data.lots.length} lot(s) booked · ${money(r.data.cost_posted, "INR", true)} posted to ${r.data.fund_code}`)
                      .then(() => setReceiving(null))}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "grn" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Book into stock
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(trace)} onOpenChange={(o) => !o && setTrace(null)}>
        <DialogContent className="max-w-xl border-border bg-card" data-testid="trace-dialog">
          <DialogHeader><DialogTitle>
            <Warehouse className="mr-1.5 inline h-4 w-4 text-primary" />
            {trace?.distribution?.distribution_ref} · back to source
          </DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(trace?.chain || []).map((c) => (
              <div key={c.lot_ref} className="rounded-xl border border-border bg-muted/20 p-3"
                   data-testid={`trace-lot-${c.lot_ref}`}>
                <div className="text-[12.5px] font-medium text-foreground">
                  {c.quantity} from lot {c.lot_ref} · batch {c.batch || "-"}
                </div>
                <div className="mt-1 text-[11.5px] text-muted-foreground">
                  Supplied by {c.supplier || "unknown"}{c.po_ref ? ` on ${c.po_ref}` : " (opening stock)"} ·
                  paid from {c.fund_code}{c.expires_on ? ` · expires ${c.expires_on}` : ""}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <DialogContent className="max-w-md border-border bg-card">
          <DialogHeader>
            <DialogTitle>Cancel {cancelling?.po_ref}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {cancelling?.supplier_name} · {money(cancelling?.total || 0, cancelling?.currency, true)}.
            Anything already received stays received and posted; only the outstanding balance is
            cancelled, and the order stays on the register with a cancelled chip.
          </p>
          <Textarea data-testid="cancel-po-reason" rows={3} value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Why is it being cancelled? (at least 10 characters)"
                    className="bg-muted/40 text-sm" />
          <Button data-testid="cancel-po-submit"
                  disabled={busy === "cancel" || cancelReason.trim().length < 10}
                  onClick={() => act("cancel",
                    () => api.post(`/supply/purchase-orders/${cancelling.po_ref}/cancel`,
                                   { reason: cancelReason }),
                    "Order cancelled and the commitment released")
                    .then(() => setCancelling(null))}
                  className="h-9 rounded-full bg-red-600 text-white hover:bg-red-600/90">
            {busy === "cancel" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Cancel the order
          </Button>
        </DialogContent>
      </Dialog>

      {detail.sheet}
    </div>
  );
}
